import { OidcClient, SigninState } from 'oidc-client-ts';

import paths from '@/lib/paths';
import sleep from '@/lib/utils/sleep';

import oidcConfig from './oidcConfig';

const REDIRECT_URI = `${import.meta.env.VITE_AUTH_HOST}/smartbuddy-callback`;
const RESULT_URL = `${import.meta.env.VITE_AUTH_HOST}/oauth/result`;

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

function randomHex(byteLength: number) {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));

  return toHex(bytes);
}

function toHex(bytes: Uint8Array) {
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}

async function sha256Hex(input: string) {
  const digest = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(input),
  );

  return toHex(new Uint8Array(digest));
}

async function createSigninState(pollToken: string) {
  const state = await SigninState.create({
    id: await sha256Hex(pollToken),
    request_type: 'si:r',
    code_verifier: true,
    authority: oidcConfig.authority,
    client_id: oidcConfig.client_id,
    redirect_uri: REDIRECT_URI,
    scope: oidcConfig.scope,
  });

  await oidcConfig.stateStore.set(state.id, state.toStorageString());

  return state;
}

async function buildAuthorizationUrl(state: SigninState) {
  const client = new OidcClient(oidcConfig);
  const authorizationEndpoint =
    await client.metadataService.getAuthorizationEndpoint();

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: state.client_id,
    redirect_uri: state.redirect_uri,
    scope: state.scope,
    state: state.id,
    ...(state.code_challenge && {
      code_challenge: state.code_challenge,
      code_challenge_method: 'S256',
    }),
  });

  return `${authorizationEndpoint}?${params}`;
}

async function fetchAuthorizationCode(pollToken: string) {
  try {
    const response = await fetch(`${RESULT_URL}?poll_token=${pollToken}`);

    if (!response.ok) return null;

    const result = (await response.json()) as { code?: string };

    return result.code ?? null;
  } catch {
    return null;
  }
}

async function pollForAuthorizationCode(
  pollToken: string,
  signal: AbortSignal,
) {
  const deadline = Date.now() + POLL_TIMEOUT_MS;

  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);

    if (signal.aborted) return null;

    const code = await fetchAuthorizationCode(pollToken);

    if (code) return code;
  }

  throw new Error('Timed out waiting for the login to complete');
}

export default async function loginWithPolling(
  onBrowserWindowUnsupported: (
    authorizationUrl: string,
    cancel: () => void,
  ) => void,
) {
  const pollToken = randomHex(16);
  const state = await createSigninState(pollToken);

  const authorizationUrl = await buildAuthorizationUrl(state);

  const canOpenBrowserWindow = Office.context.requirements.isSetSupported(
    'OpenBrowserWindowApi',
    '1.1',
  );

  // Hosts WITHOUT openBrowserWindow (e.g. new Outlook for Windows) can only
  // open Outlook-controlled webview windows, which freeze on OAuth redirects
  // (https://github.com/MicrosoftEdge/WebView2Feedback/issues/5630).
  // Let the caller show the URL for manual opening.
  const abortController = new AbortController();

  if (canOpenBrowserWindow) {
    Office.context.ui.openBrowserWindow(authorizationUrl);
  } else {
    onBrowserWindowUnsupported(authorizationUrl, () => abortController.abort());
  }

  const code = await pollForAuthorizationCode(
    pollToken,
    abortController.signal,
  );

  if (!code) return 'cancelled';

  // Hand the code to react-oidc-context
  const url = new URL(window.location.href);
  url.pathname = paths.login.index;
  url.search = new URLSearchParams({ code, state: state.id }).toString();

  window.location.href = url.toString();

  return 'success';
}
